import { endOfDay } from 'date-fns'

import { tokenDecode } from '@subaio/utils'

import type { User } from 'apiTypes/User'

import type { UserAuth } from 'lib/store'

export const getUserAuth = (user?: Omit<User, 'expiration' | 'userId' | 'brandId'>): UserAuth => {
	if (!user) return { status: 'unauthenticated' }

	const { exp, sub, brandId } = tokenDecode(user.token)
	if (typeof exp !== 'number' || !sub || !brandId) {
		return { status: 'unauthenticated' }
	}

	const now = Date.now()

	const expiration = Math.min(exp * 1000, endOfDay(now).valueOf())

	if (expiration < now) {
		return { status: 'expired' }
	}

	return {
		status: 'authenticated',
		user: {
			...user,
			userId: sub,
			brandId,
			expiration: exp,
		},
	}
}

/**
 * Throw if the user isn't authenticated. This should only be used when the user
 * is expected (all but guaranteed) to be authenticated, such as in a container
 * requiring authentication.
 */
export const assertAuthenticated: (
	userAuth: UserAuth,
) => asserts userAuth is Extract<UserAuth, { status: 'authenticated' }> = (userAuth) => {
	if (userAuth.status !== 'authenticated') throw new Error('Expected user to be authenticated')
}
